import React, { useState, useEffect, useRef, useId } from "react";
import { useTranslation } from "react-i18next";
import { Search, ChevronDown, Check, X } from "lucide-react";

interface FilterOption {
    value: string;
    label: string;
    subLabel?: string;
    avatar?: string; // URL for avatar image
    icon?: React.ReactNode;
}

interface FilterSelectProps {
    options: FilterOption[];
    value: string | string[]; // Can be string or array
    onChange: (value: string | string[]) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    label?: string;
    className?: string;
    disabled?: boolean;
    icon?: React.ReactNode;
    multiple?: boolean;
    allowCustomValues?: boolean;
    addCustomLabel?: string;
    /**
     * Id of the visible label naming this control. A `<label htmlFor>` cannot
     * name the trigger, which is not a form element. Without it (or
     * `aria-label`, or `label`), the placeholder names the control.
     */
    "aria-labelledby"?: string;
    "aria-label"?: string;
}

type Name = { "aria-labelledby": string } | { "aria-label": string };

/**
 * A searchable select, operated as a WAI-ARIA combobox: the trigger opens a
 * popup whose search box owns the listbox through `aria-activedescendant`, so
 * the keyboard never leaves the text field while it moves through the options.
 */
const FilterSelect: React.FC<FilterSelectProps> = ({
    options,
    value,
    onChange,
    placeholder,
    searchPlaceholder,
    label,
    className = "",
    disabled = false,
    icon,
    multiple = false,
    allowCustomValues = false,
    addCustomLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-label": ariaLabel,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeIndex, setActiveIndex] = useState(-1);
    // Whether the active option was reached with the keyboard, which marks it
    // with a ring; under the pointer the hover tint is enough.
    const [keyboardActive, setKeyboardActive] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const baseId = useId();
    const labelId = `${baseId}-label`;
    const listboxId = `${baseId}-listbox`;
    const optionId = (index: number) => `${baseId}-option-${index}`;

    const placeholderText = placeholder ?? t("common.select");
    const searchPlaceholderText = searchPlaceholder ?? t("common.filterSelect.searchPlaceholder");
    const addCustomText = addCustomLabel ?? t("common.add");

    const name: Name = ariaLabelledby
        ? { "aria-labelledby": ariaLabelledby }
        : ariaLabel
          ? { "aria-label": ariaLabel }
          : label
            ? { "aria-labelledby": labelId }
            : { "aria-label": placeholderText };

    // Ensure value is treated as an array for multi-select, or single value for single-select context
    const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

    // For display: find option objects for selected values (may include custom values not in options)
    const selectedOptions = selectedValues.map(v => {
        const found = options.find(opt => opt.value === v);
        return found ?? { value: v, label: v };
    });

    // For single select display
    const singleSelectedOption = !multiple && selectedValues.length > 0
        ? (options.find(opt => opt.value === selectedValues[0]) ?? { value: selectedValues[0], label: selectedValues[0] })
        : null;

    // Filter options based on search term
    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (option.subLabel && option.subLabel.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const trimmedSearch = searchTerm.trim();
    const showAddCustom = allowCustomValues &&
        trimmedSearch.length > 0 &&
        !options.some(o => o.label.toLowerCase() === trimmedSearch.toLowerCase() || o.value.toLowerCase() === trimmedSearch.toLowerCase()) &&
        !selectedValues.includes(trimmedSearch);

    // The "add custom" row is the last navigable item after the options.
    const itemCount = filteredOptions.length + (showAddCustom ? 1 : 0);
    const current = Math.min(activeIndex, itemCount - 1);

    const openList = (start: "selected" | "first" | "last" = "selected", initialSearch = "") => {
        if (disabled) return;
        setSearchTerm(initialSearch);
        const selectedIndex = options.findIndex(o => selectedValues.includes(o.value));
        setActiveIndex(
            start === "last" ? options.length - 1
                : start === "first" || selectedIndex < 0 ? 0
                    : selectedIndex,
        );
        setKeyboardActive(true);
        setIsOpen(true);
    };

    const closeList = (returnFocus: boolean) => {
        setIsOpen(false);
        setSearchTerm("");
        setActiveIndex(-1);
        if (returnFocus) triggerRef.current?.focus();
    };

    const handleAddCustom = () => {
        if (!trimmedSearch) return;
        if (multiple) {
            const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
            onChange([...currentValues, trimmedSearch]);
            setSearchTerm("");
        } else {
            onChange(trimmedSearch);
            closeList(true);
        }
    };

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm("");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen) inputRef.current?.focus();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || current < 0) return;
        document.getElementById(`${baseId}-option-${current}`)?.scrollIntoView?.({ block: "nearest" });
    }, [isOpen, current, baseId]);

    const handleSelect = (optionValue: string) => {
        if (multiple) {
            const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
            // Don't close on select for multiple
            const newValue = currentValues.includes(optionValue)
                ? currentValues.filter(v => v !== optionValue)
                : [...currentValues, optionValue];
            onChange(newValue);
        } else {
            onChange(optionValue);
            closeList(true);
        }
    };

    const removeValue = (e: React.MouseEvent, valToRemove: string) => {
        e.stopPropagation();
        if (multiple) {
            const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
            onChange(currentValues.filter(v => v !== valToRemove));
        } else {
            onChange("");
        }
    };

    const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (isOpen && e.key === "Escape") {
            e.preventDefault();
            e.nativeEvent.stopPropagation();
            closeList(true);
            return;
        }
        switch (e.key) {
            case "Enter":
            case " ":
            case "ArrowDown":
            case "ArrowUp":
                e.preventDefault();
                openList();
                return;
            case "Home":
                e.preventDefault();
                openList("first");
                return;
            case "End":
                e.preventDefault();
                openList("last");
                return;
        }
        // Typing on the closed trigger starts a search with that character.
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            openList("first", e.key);
        }
    };

    const moveTo = (index: number) => {
        setActiveIndex(index);
        setKeyboardActive(true);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                if (itemCount > 0) moveTo(Math.min(current + 1, itemCount - 1));
                return;
            case "ArrowUp":
                e.preventDefault();
                if (itemCount > 0) moveTo(Math.max(current - 1, 0));
                return;
            case "Home":
                if (itemCount === 0) return;
                e.preventDefault();
                moveTo(0);
                return;
            case "End":
                if (itemCount === 0) return;
                e.preventDefault();
                moveTo(itemCount - 1);
                return;
            case "Enter":
                // Never submit the surrounding form from the search box.
                e.preventDefault();
                if (current < 0) return;
                if (current < filteredOptions.length) handleSelect(filteredOptions[current].value);
                else if (showAddCustom) handleAddCustom();
                return;
            case "Escape":
                // Close only the list, not a dialog this select sits in.
                e.preventDefault();
                e.nativeEvent.stopPropagation();
                closeList(true);
                return;
            case "Tab":
                if (e.shiftKey) {
                    e.preventDefault();
                    closeList(true);
                }
                // A forward Tab leaves the popup; the wrapper's blur closes it.
                return;
            case "Backspace":
                if (multiple && searchTerm === "" && selectedValues.length > 0) {
                    e.preventDefault();
                    onChange(selectedValues.slice(0, -1));
                }
                return;
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        const next = e.relatedTarget as Node | null;
        if (isOpen && next && !wrapperRef.current?.contains(next)) {
            setIsOpen(false);
            setSearchTerm("");
        }
    };

    const optionClasses = (isSelected: boolean, isActive: boolean) => `
                      flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-colors
                      ${isSelected
            ? "bg-accent-tint text-accent-text"
            : isActive
                ? "bg-gray-100 text-gray-900 dark:bg-gray-700/50 dark:text-gray-100"
                : "text-gray-700 dark:text-gray-200"}
                      ${isActive && keyboardActive ? "ring-2 ring-inset ring-accent-text" : ""}
                    `;

    return (
        <div className={`w-full group ${className}`} ref={wrapperRef} onBlur={handleBlur}>
            {label && (
                <label id={labelId} className="block text-xs font-semibold text-content-tertiary uppercase tracking-wider mb-2">
                    {label}
                </label>
            )}

            <div className="relative">
                {/* Trigger */}
                <div
                    ref={triggerRef}
                    role="combobox"
                    tabIndex={disabled ? -1 : 0}
                    {...name}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? listboxId : undefined}
                    aria-disabled={disabled || undefined}
                    onClick={() => {
                        if (disabled) return;
                        if (isOpen) closeList(false);
                        else openList();
                    }}
                    onKeyDown={handleTriggerKeyDown}
                    className={`
            relative flex items-center w-full px-4 py-3 border rounded-xl bg-gray-50 text-left cursor-pointer transition-all duration-200 min-h-[50px]
            ${isOpen ? "ring-2 ring-accent/20 border-accent bg-white" : "border-gray-200 hover:bg-white hover:border-gray-300"}
            ${disabled ? "opacity-50 cursor-not-allowed bg-gray-100" : ""}
            dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100
          `}
                >
                    {/* Icon (only show if single select or empty) */}
                    {(!multiple || selectedOptions.length === 0) && (
                        <div aria-hidden="true" className="mr-3 text-content-tertiary group-focus-within:text-accent-text transition-colors">
                            {icon || <Search className="w-5 h-5" />}
                        </div>
                    )}

                    {/* Value Display */}
                    {/* min-w-0: a long label or placeholder truncates instead of
                        pushing the field wider than its column. */}
                    <div className="flex-1 min-w-0 flex flex-wrap gap-2 items-center">
                        {multiple && selectedOptions.length > 0 ? (
                            selectedOptions.map(opt => (
                                <div key={opt.value} className="flex items-center bg-accent-tint text-accent-text px-2 py-1 rounded-md text-sm animate-in fade-in zoom-in-95 duration-100">
                                    <span>{opt.label}</span>
                                    {/* Pointer shortcut; the keyboard deselects in the list or with Backspace. */}
                                    <div
                                        aria-hidden="true"
                                        onClick={(e) => removeValue(e, opt.value)}
                                        className="ml-1 cursor-pointer rounded-full p-0.5 hover:bg-accent-tint hover:text-accent-on-tint"
                                    >
                                        <X className="w-3 h-3" />
                                    </div>
                                </div>
                            ))
                        ) : singleSelectedOption ? (
                            <span className="text-gray-900 dark:text-gray-100 font-medium flex min-w-0 items-center gap-2">
                                {singleSelectedOption.icon && <span aria-hidden="true" className="text-content-tertiary">{singleSelectedOption.icon}</span>}
                                <span className="truncate" title={singleSelectedOption.label}>{singleSelectedOption.label}</span>
                            </span>
                        ) : (
                            <span className="block truncate text-content-tertiary">{placeholderText}</span>
                        )}
                    </div>

                    {/* Chevron */}
                    <ChevronDown aria-hidden="true" className={`ml-2 w-5 h-5 text-content-tertiary transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </div>

                {/* Dropdown Menu */}
                {isOpen && (
                    <div className="absolute z-50 left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top">

                        {/* Search Input inside Dropdown */}
                        <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                            <div className="relative">
                                <Search aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    role="combobox"
                                    aria-label={searchPlaceholderText}
                                    aria-autocomplete="list"
                                    aria-expanded="true"
                                    aria-controls={listboxId}
                                    aria-activedescendant={current >= 0 ? optionId(current) : undefined}
                                    autoComplete="off"
                                    className="w-full rounded-md border-none bg-surface-sunken py-2 pl-9 pr-4 text-sm text-content-primary placeholder:text-content-tertiary"
                                    placeholder={searchPlaceholderText}
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        moveTo(0);
                                    }}
                                    onKeyDown={handleInputKeyDown}
                                    // Stop propagation to prevent closing dropdown when clicking input
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>

                        {/* Options List */}
                        <div className="max-h-60 overflow-y-auto p-1 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
                            <div
                                id={listboxId}
                                role="listbox"
                                {...name}
                                aria-multiselectable={multiple || undefined}
                            >
                                {filteredOptions.map((option, index) => {
                                    const isSelected = selectedValues.includes(option.value);

                                    return (
                                        <div
                                            key={option.value}
                                            id={optionId(index)}
                                            role="option"
                                            aria-selected={isSelected}
                                            // Keep focus in the search box so the keyboard carries on.
                                            onMouseDown={(e) => e.preventDefault()}
                                            onMouseEnter={() => {
                                                setActiveIndex(index);
                                                setKeyboardActive(false);
                                            }}
                                            onClick={() => handleSelect(option.value)}
                                            className={optionClasses(isSelected, index === current)}
                                        >
                                            <div className="flex items-center gap-2">
                                                {option.icon && <span aria-hidden="true" className="text-content-tertiary">{option.icon}</span>}
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-sm">{option.label}</span>
                                                    {option.subLabel && (
                                                        <span className="text-xs text-content-tertiary">{option.subLabel}</span>
                                                    )}
                                                </div>
                                            </div>
                                            {isSelected && <Check aria-hidden="true" className="w-4 h-4" />}
                                        </div>
                                    );
                                })}
                                {showAddCustom && (
                                    <div
                                        id={optionId(filteredOptions.length)}
                                        role="option"
                                        aria-selected={false}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onMouseEnter={() => {
                                            setActiveIndex(filteredOptions.length);
                                            setKeyboardActive(false);
                                        }}
                                        onClick={handleAddCustom}
                                        className={`mt-1 flex cursor-pointer items-center gap-2 rounded-md border-t border-border-subtle px-3 py-2.5 text-accent-text transition-colors hover:bg-accent-tint ${current === filteredOptions.length ? "bg-accent-tint" : ""} ${current === filteredOptions.length && keyboardActive ? "ring-2 ring-inset ring-accent-text" : ""}`}
                                    >
                                        <span aria-hidden="true" className="font-semibold text-sm">+</span>
                                        <span className="text-sm">{addCustomText} "<strong>{trimmedSearch}</strong>"</span>
                                    </div>
                                )}
                            </div>
                            <div role="status">
                                {itemCount === 0 && (
                                    <div className="px-4 py-6 text-center text-sm text-content-tertiary">
                                        {searchTerm
                                            ? t("common.filterSelect.noMatches", { query: searchTerm })
                                            : t("common.filterSelect.noOptions")}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FilterSelect;
